import UAParser from 'ua-parser-js';
import { incline } from 'lvovich';
import format from 'date-fns/format';

import { groupBy } from 'lodash';
import { EMOJI_REGEX } from 'shared/constants';
import { Api } from 'api';
import { logEvent, setUserId } from 'firebase/analytics';
import { t } from '$translation/helper';
import {
  AttachType,
  CurrencyEnum,
  ImportedModule,
  NotifyModel,
  PostModel,
  UserModel,
} from '$store/models';
import { transliterate } from '$utils/transliterate';
import {
  getVirtuosoEntry,
  isVirtuosoAtBottom,
  scrollVirtuosoToBottom,
} from '$utils/virtuosoRegistry';
import { DeclentionStrT, NOMINATIVE } from 'lvovich/lib/inclineRules';
import { getStorageItem, getToken } from '$utils/asyncStorage';

import { _confirm } from '$utils/confirm';
import { ContentPriorityType } from '$store/main/interface';
import { aesDecrypt } from '$utils/crypto';
import router from '$navigation/router';
import { CANDY_QR, VERIFICATION_APPLY } from '$shared/constants/pages';
import { prettifyNumber } from '$utils/number';
import WebApp from '@twa-dev/sdk';

const uaParser = new UAParser();

export function smartFeedEnabled(userId: number, contentPriority): boolean {
  if (contentPriority === 'adult') {
    return false;
  }

  if ([1, 39469, 3, 7, 136476, 156525, 87344].indexOf(userId) > -1) {
    return true;
  }

  return true; //userId % 2 === 0;
}

export const chunk = (input: any[], size: number) => {
  return input.reduce((arr, item, idx) => {
    return idx % size === 0
      ? [...arr, [item]]
      : [...arr.slice(0, -1), [...arr.slice(-1)[0], item]];
  }, []);
};

export function shuffle(a: any[]) {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function getRandomInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min)) + min;
}

export function isAdminPanel(): boolean {
  // @ts-ignore
  return !!window.adminPanel;
}

export function capitalize(str: string) {
  return str.substring(0, 1).toUpperCase() + str.substring(1);
}

export function genderText(gender: number, variants: string[]) {
  // gender 2 is female
  return +gender === 2 ? variants[0] : variants[1];
}

export function classNames(...args) {
  let result: any[] = [];

  // @ts-ignore
  [].concat(args).forEach(function (item) {
    if (!item) {
      return;
    }
    switch (typeof item === 'undefined' ? 'undefined' : typeof item) {
      case 'string':
        result.push(item);
        break;
      case 'object':
        Object.keys(item).forEach(function (key) {
          if (item[key]) {
            result.push(key);
          }
        });
        break;
      default:
        result.push('' + item);
    }
  });

  return result.join(' ');
}

export function ily(yr) {
  return !(yr % 4 || (!(yr % 100) && yr % 400));
}

export function roundNumber(input, decimals = 2) {
  const roundTo = Math.pow(10, decimals);
  return parseInt((input * roundTo).toString()) / roundTo;
}

export function roundCryptoNumber(input, decimals = 2) {
  return input > 1
    ? roundNumber(input, decimals)
    : eval(input)
        .toPrecision(decimals)
        .replace(/(?:\.0+|(\.\d+?)0+)$/, '$1');
}

export function formatBigNumber(input, decimals = 1) {
  if (input < 1000) {
    return input;
  } else if (input < 1000000) {
    return `${roundNumber(input / 1000, decimals)}K`;
  } else {
    return `${roundNumber(input / 1000000, decimals)}M`;
  }
}

export function gram(number, variants, skipNumber = false): string {
  const cases = [2, 0, 1, 1, 1, 2];
  let res =
    variants[
      number % 100 > 4 && number % 100 < 20 ? 2 : cases[number % 10 < 5 ? number % 10 : 5]
    ];
  if (!skipNumber) {
    res = number + ' ' + res;
  }
  return res;
}

export function validateEmail(email) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export function validatePhone(phone) {
  return `${phone}`.length >= 8;
}

export function validatePassword(password) {
  return `${password}`.length >= 8;
}

export function releaseVideo(video: HTMLVideoElement) {
  video.pause();
  video.removeAttribute('src');
  video.load();
  video.remove();
}

export function releaseCanvas(canvas: HTMLCanvasElement) {
  canvas.width = 1;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  ctx && ctx.clearRect(0, 0, 1, 1);
  canvas.remove();
}

export function canvasToBlob(canvas, quality = 1): Promise<Blob> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });
}

export const imageLoad = (url: string) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.src = url;
  });

export function scrollToBottom(elemId, retryLimit = 0) {
  if (scrollVirtuosoToBottom(elemId)) {
    return;
  }

  const elem = document.getElementById(elemId);
  if (!elem) {
    return;
  }

  // hack tp-883
  if (
    elem.scrollTop === 0 &&
    elem.scrollHeight - elem.offsetHeight === 0 &&
    retryLimit > 0
  ) {
    setTimeout(() => {
      scrollToBottom(elemId, retryLimit - 1);
    }, 100);
  }

  elem.scrollTop = elem.scrollHeight - elem.offsetHeight;
}

export function haptic(style: 'light' | 'medium' | 'heavy' = 'light') {
  try {
    if (window.isWebApp) {
      (WebApp as any).HapticFeedback?.impactOccurred(style);
    } else if (navigator.vibrate) {
      navigator.vibrate({ light: 10, medium: 20, heavy: 30 }[style]);
    }
  } catch (e) {}
}

export function scrollToBottomWithCheck(elemId) {
  const virtuoso = getVirtuosoEntry(elemId);
  if (virtuoso) {
    if (isVirtuosoAtBottom(elemId)) {
      scrollVirtuosoToBottom(elemId);
    }
    return;
  }

  const elem = document.getElementById(elemId);
  if (!elem) {
    return;
  }

  const endScroll = elem.scrollHeight - elem.offsetHeight;
  const curScroll = elem.scrollTop;

  if (endScroll - curScroll < 600) {
    scrollToBottom(elemId);
  }
}

export function isDesktop() {
  return window.innerWidth >= 1000;
}

interface StatQueueItem {
  name: string;
  params: any;
}

let StatsQueue: StatQueueItem[] = [];

const statReachGoalSupportedHostnames = new Set<string>(['tpc.fan', 'ton.place']);
const statReachGoalUnsupportedHostnames = new Set<string>();

export function statReachGoal(eventName: string, params: any = {}) {
  if (window.store) {
    const { main } = window.store.getState();
    if (!statReachGoalSupportedHostnames.has(location.hostname)) {
      if (!statReachGoalUnsupportedHostnames.has(location.hostname)) {
        console.warn(`statReachGoal is not supported on "${location.hostname}"`);
        statReachGoalUnsupportedHostnames.add(location.hostname);
      }
      return;
    }
    if (main && main.contentPriority === ContentPriorityType.OTHER) {
      return;
      // if (
      //   [
      //     'registration',
      //     'user_visit',
      //     'any_media_user',
      //     'media_user',
      //     'free_media_user',
      //   ].indexOf(eventName) === -1
      // ) {
      //   return;
      // }
    }
  }

  try {
    // @ts-ignore
    if (window.isYAInited) {
      if (StatsQueue.length > 0) {
        for (let event of StatsQueue) {
          stat(event.name, event.params);
          statYandex(event.name, event.params);
          gaEvent(event.name, event.params);
        }
      }

      // ya_inited need only for resolve queue
      if (eventName === 'ya_inited') {
        return;
      }

      StatsQueue = [];
      stat(eventName, params);
      statYandex(eventName, params);
      gaEvent(eventName, params);
    } else {
      StatsQueue.push({
        name: eventName,
        params,
      });
      return;
    }
  } catch (e) {}
}

export function trackPurchase(amount: number, action: string, itemId: string) {
  statReachGoal('purchase', {
    currency: 'EUR',
    value: +amount,
    action,
    items: [
      {
        item_id: `${action}_${itemId}`,
        price: +amount,
        item_name: action,
        currency: 'EUR',
      },
    ],
  });

  try {
    // @ts-ignore
    window.fbq('track', 'Purchase', {
      currency: 'EUR',
      value: +amount,
    });
  } catch (e) {}
}

let analytics: any = null;

export function setAnalytics(inst) {
  analytics = inst;
}

export function firebaseSetUserId(userId: number) {
  try {
    window.gtag('config', 'GA_MEASUREMENT_ID', {
      user_id: `${userId}`,
    });
  } catch (e) {}

  if (!analytics) {
    return;
  }
  setUserId(analytics, `${userId}`, {
    global: true,
  });
}

export const gaEvent = (action: string, params: any) => {
  try {
    // console.log('gaEvent: ', action, params);
    window.gtag('event', action, params);
  } catch (e) {
    console.error('gaEvent error', e);
  }
};

function statYandex(eventName: string, params: any = {}) {
  // console.log('statYandex', eventName, params);
  try {
    /* @ts-ignore */
    window.ym(87337659, 'reachGoal', eventName, params);
    if (analytics) {
      logEvent(analytics, eventName, params);
    }
  } catch (e) {}
}

function stat(eventName: string, params: any) {
  const { token } = getToken();
  if (eventName === 'ya_inited' || !token) {
    return;
  }

  const query =
    Object.values(params)
      .map((value) => {
        // @ts-ignore
        return `value=${encodeURIComponent(value.toString())}`;
      })
      .join('&') || 'value=0';

  eventName !== 'network_error' &&
    Api.get(`/stats/user_goal?name=${eventName}&${query}`).catch((r) => {
      console.error(r.error);
    });
}

export function isNative(version = 0) {
  // @ts-ignore
  if (!window.isNative) {
    return false;
  }

  // @ts-ignore
  return window.nativeVersion >= version;
}

export function isNotificationsSupported() {
  return !!window.Notification;
}

export function canShowNotifyRequest() {
  const lastNotifyShownAt = +(getStorageItem('lastNotifyRequestShown') || 0);
  return isNotificationsSupported() && Date.now() - lastNotifyShownAt > 86400 * 3 * 1000;
}

export function canShowNotifyOnRoute() {
  const route = router.getState();
  return ![CANDY_QR, VERIFICATION_APPLY].includes(route.name);
}

export function getRatingColor(karma) {
  if (karma >= 10000) {
    return 'red';
  } else if (karma >= 5000) {
    return 'purple';
  } else if (karma >= 2500) {
    return 'orange';
  } else if (karma >= 1000) {
    return 'green';
  } else {
    return 'cyan';
  }
}

export function getRatingNextLevelValue(karma) {
  if (karma >= 10000) {
    return 10000;
  } else if (karma >= 5000) {
    return 10000;
  } else if (karma >= 2500) {
    return 5000;
  } else if (karma >= 1000) {
    return 2500;
  } else {
    return 1000;
  }
}

export function isPWA() {
  return isIOS()
    ? window.navigator['standalone'] === true
    : uaParser.getDevice().type === 'mobile' &&
        window.matchMedia('(display-mode: standalone)').matches;
}

export function isIOS() {
  const userAgent = navigator.userAgent.toLowerCase();
  return /ipad|iphone|ipod/.test(userAgent);
  //return uaParser.getOS().name?.toLowerCase() === 'ios';
}

export function isAndroid() {
  const userAgent = navigator.userAgent.toLowerCase();
  return /android/.test(userAgent);
  //return uaParser.getOS().name?.toLowerCase() === 'android';
}

export function canInstallPWA() {
  return !isSafari() && !isIOS();
}

export function isSafari() {
  return ['Safari', 'Mobile Safari'].includes(uaParser.getBrowser().name || '');
}

export function internalLink(link: string) {
  return !!link.match(/^(https?:\/\/)?(ton)\.(place)/i);
}

const TRUSTED_DOMAINS = ['ton.place', 'tonplace.host', 'tonplace.net'];

export function isTrustedDomain(url: string): boolean {
  try {
    const normalized = /^https?:\/\//i.test(url) ? url : 'https://' + url;
    const hostname = new URL(normalized).hostname.toLowerCase();
    return TRUSTED_DOMAINS.some(
      (domain) => hostname === domain || hostname.endsWith('.' + domain),
    );
  } catch {
    return false;
  }
}

export function findScam(link: string) {
  return !!link.match(/rocketon/i);
}

export function handleScam(e) {
  _confirm(t('link_scam'), () => true);

  try {
    e.stopPropagation();
    e.preventDefault();
  } catch (e) {}
  return false;
}

// @ts-ignore
window.handleScam = handleScam;

export function formatFileSize(fileSizeInBytes: number) {
  const i =
    fileSizeInBytes === 0 ? 0 : Math.floor(Math.log(fileSizeInBytes) / Math.log(1024));
  return (
    Number((fileSizeInBytes / Math.pow(1024, i)).toFixed(2)) +
      ' ' +
      ['B', 'KB', 'MB', 'GB'][i] || 'GB'
  );
}

function formatLocalTime(time: number) {
  return time < 10 ? `0${time}` : time;
}

export function getFormattedTime(timeInSeconds: number) {
  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor((timeInSeconds % 3600) / 60);
  const seconds = Math.floor(timeInSeconds % 60);

  return (
    (hours > 1 ? `${formatLocalTime(hours)}:` : '') +
    `${formatLocalTime(minutes)}:${formatLocalTime(seconds)}`
  );
}

export function getDistanceBetweenTwoElements(
  leftElement: HTMLElement,
  rightElement: HTMLElement,
) {
  const leftRect = leftElement.getBoundingClientRect();
  const rightRect = rightElement.getBoundingClientRect();

  return rightRect.left - leftRect.right;
}

export function cutNumberFractionalPart(num: number, fractionalLength = 2) {
  const [integer, fractional] = num.toString().split('.');

  if (!fractional || fractional.length <= 1) {
    return num;
  }

  return `${integer}.${fractional.slice(
    0,
    fractional[fractionalLength - 1] === '0' ? fractionalLength - 1 : fractionalLength,
  )}`;
}

export function importAllFromDirectory(exportContext): ImportedModule {
  return exportContext.keys().reduce((images, item) => {
    images[item.replace('./', '')] = exportContext(item);
    return images;
  }, {});
}

export const toPrecisionWithoutRounding = (
  num: number,
  precise?: number,
  maximumFractionDigits: number = 20,
): string => {
  const stringifiedNumber = num.toLocaleString('en-US', {
    useGrouping: false,
    maximumFractionDigits,
  });

  if (!precise || Number.isInteger(num)) {
    return stringifiedNumber;
  }

  const preciseRegExp = new RegExp(`\\d+(.0*[1-9]{1,${precise}})?`);

  const matchedRegExpResult = stringifiedNumber.match(preciseRegExp);

  return matchedRegExpResult ? matchedRegExpResult[0] : '0';
};

export function formatCurrency(
  num: string | number = 0,
  currency: string,
  {
    symbol,
    precise,
    maximumFractionDigits,
  }: { symbol?: boolean; precise?: number; maximumFractionDigits?: number } = {
    symbol: true,
  },
): string {
  if (currency === CurrencyEnum.PLACE) {
    return `${prettifyNumber(+num)} ${symbol ? currency.toUpperCase() : ''}`;
  }

  const isFiat =
    currency.toLowerCase() === CurrencyEnum.EUR ||
    currency.toLowerCase() === CurrencyEnum.USD;

  let precises = precise || maximumFractionDigits || (isFiat ? 2 : 6);

  if (currency === CurrencyEnum.PLACE) {
    precises = 0;
  }

  if (isFiat) {
    return new Intl.NumberFormat('en-EN', {
      style: symbol ? 'currency' : undefined,
      currency: symbol ? currency.toUpperCase() : undefined,
      maximumFractionDigits: precises,
    })
      .format(roundNumber(num, precises))
      .replace(/\.00.*/gm, '')
      .replace(/\,/gm, ' ');
  }

  return `${roundNumber(num, precises)} ${symbol ? currency.toUpperCase() : ''}`;
}

export const isSpecialModelId = (id: number) => {
  return [
    173899, // Olya Lovnse
  ].includes(id);
};

export const isTesterId = (id: number) => {
  return [
    1, // Andrew Goglev
    3, // Des Ign
    8, // Denis Borodin
    178133, // Denis Denis
    296899, // Lime Impala (Denis)
    296166, // Aqua Pelican (Denis)
    573988, // Pink Sailfish (Denis)
    163935, // adonis vernalis
    156525, // Юлия Кусь
    179265, // Jules Vaughn
    172708, // Hey Man
    136476, // Scary Stories
    170903, // Dogina Mil
    165195, // Nikola Fet
    152482, // Mark Erov
    296039, // Rr Sss
    702035,
    164670,
    3218813, // YuriyU
    3257526, // YuriyU
    239, // YuriyU
    2320215, // @oleg_kotik42
    2993371, // Hayk
    2339240,
    1199380, // @DeusDeveloper
  ].includes(id);
};

let webpSupport: boolean | undefined;

export const isWebpSupported = () => {
  if (typeof webpSupport !== 'undefined') return webpSupport;

  const canvas = document.createElement('canvas');
  webpSupport = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  return webpSupport;
};

export const getCaptchaToken: () => Promise<string> = () =>
  new Promise((resolve, reject) => {
    dispatchEvent(new CustomEvent('showCaptcha'));
    const successCallback = (e) => {
      const { token } = e.detail;
      if (token) {
        resolve(token);
      } else {
        reject();
      }
      removeEventListener('captchaChange', successCallback);
    };
    const rejectCallback = (e) => {
      reject();
      removeEventListener('captchaReject', rejectCallback);
    };
    addEventListener('captchaChange', successCallback);
    addEventListener('captchaReject', rejectCallback);
  });

export const inclineUserName = (
  user: UserModel,
  d: DeclentionStrT = NOMINATIVE,
  lang = 'ru',
) => {
  if (lang === 'ru') {
    const u = incline(
      {
        gender: user.sex ? 'female' : 'male',
        first: user.firstName ?? '',
        last: user.lastName ?? '',
      },
      d,
    );

    return {
      first: u.first,
      last: u.last,
      full: [u.first, u.last].join(' '),
    };
  }
  return {
    first: transliterate(user.firstName),
    last: transliterate(user.firstName),
    full: [user.firstName, user.lastName].map(transliterate).join(' '),
  };
};

export const linkIsValid = (link: string): boolean => {
  try {
    new URL(link);
    return true;
  } catch (e) {
    return false;
  }
};
export const createInviteLink = (
  link: string,
  userId: number,
  linkId: number,
): string | null => {
  try {
    // const hasHttp = link.indexOf('http') === 0;
    if (!link.includes('ton.place')) {
      return null;
    }
    const url = new URL(link);
    const params = new URLSearchParams(url.search);
    params.append('utm_source', `user_${userId}`);
    params.append('utm_campaign', `link_${linkId}`);
    url.search = params.toString();
    return url.toString();
  } catch (e) {
    return null;
  }
};

export const experiment = (userId: number, name: string, options: string[]): string => {
  const option = options[userId % options.length];
  // window.gtag('event', `experiment_${name}`, {
  //   option: option,
  // });
  return option;
};

interface RolloutConfig {
  divider: number;
  include?: number[];
  exclude?: number[];
}

/**
 * Percentage-based feature rollout with whitelist/blacklist.
 *
 *   rollout(userId, { divider: 2 })                   → 50% (even IDs)
 *   rollout(userId, { divider: 4 })                   → 25%
 *   rollout(userId, { divider: 2, include: [183504] }) → 50% + always on for 183504
 *   rollout(userId, { divider: 2, exclude: [999] })    → 50% but always off for 999
 *
 * Testers (isTesterId) are always included.
 * `exclude` takes priority over `include` and testers.
 */
export const rollout = (userId: number, config: RolloutConfig): boolean => {
  if (config.exclude?.includes(userId)) return false;
  if (isTesterId(userId)) return true;
  if (config.include?.includes(userId)) return true;
  return userId % config.divider === 0;
};

export const getConfirmCode: () => Promise<string> = () =>
  new Promise((resolve, reject) => {
    dispatchEvent(new CustomEvent('showConfirmCode'));
    const successCallback = (e) => {
      const { confirmCode } = e.detail;
      if (confirmCode) {
        resolve(confirmCode);
      } else {
        reject();
      }
      removeEventListener('confirmCodeSubmit', successCallback);
    };
    const rejectCallback = () => {
      reject();
      removeEventListener('confirmCodeReject', successCallback);
    };
    addEventListener('confirmCodeSubmit', successCallback);
    addEventListener('confirmCodeReject', rejectCallback);
  });

export function retry(fn, retriesLeft = 5, interval = 1000): Promise<any> {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            // reject('maximum retries exceeded');
            reject(error);
            return;
          }

          // Passing on "reject" is the important part
          retry(fn, retriesLeft - 1, interval).then(resolve, reject);
        }, interval);
      });
  });
}

let isChristmasHolidaysValue: boolean | null = null;
export const isChristmasHolidays = () => {
  if (isChristmasHolidaysValue !== null) {
    return isChristmasHolidaysValue;
  }
  const today = new Date();
  const month = today.getMonth();
  const day = today.getDate();

  return (month === 11 && day > 12) || (month === 0 && day < 11);
};

let isBlackFridayValue: boolean | null = null;
export const isBlackFriday = () => {
  return false;
  // if (isBlackFridayValue !== null) {
  //   return isBlackFridayValue;
  // }
  // const today = new Date();
  // const blackFridayDates = [
  //   new Date(2022, 10, 25),
  //   new Date(2022, 11, 2),
  //   new Date(2022, 11, 9),
  //   new Date(2022, 11, 16),
  //   new Date(2022, 11, 23),
  // ];
  //
  // isBlackFridayValue = blackFridayDates.some((date) => isSameDay(today, date));
  // return isBlackFridayValue;
};

export const getCssVar = (name: string) => {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
};

export const groupedNotifyByDate = (notifications: NotifyModel[], lang) => {
  const grouped = groupBy(notifications, (n) => {
    return format(n.createdAt * 1000, `MM.dd.yyyy`);
  });
  return Object.keys(grouped).map((key) => {
    // const date = parse(key, `dd.MM.yyyy`, new Date()).getTime();
    const date = new Date(key).getTime();
    return {
      date,
      notifications: grouped[key],
    };
  });
};

export const parsePostText = (text: string, limit = 0): string => {
  let preparedText = '';
  if (!text) {
    text = '';
  }
  if (text.substring && text.substring(0, 1) === '{') {
    try {
      const ops = JSON.parse(text).ops;
      let rows: string[] = [];
      for (let i = 0; i < ops.length; i++) {
        rows.push(ops[i].insert);
      }
      preparedText = rows.join(' ').replaceAll('\n', ' ').trim();
    } catch (error) {
      return '';
    }
  } else {
    preparedText = text;
  }

  if (limit && preparedText.length > limit) {
    return preparedText.substring(0, limit).trim() + '...';
  }

  return preparedText;
};

export const getImageFromPost = (post: PostModel, decryptKey = ''): string[] => {
  if (!post || !post?.attachments?.length) {
    return [];
  }
  let sources: string[] = [];
  for (const attach of post.attachments) {
    if (attach.type === AttachType.PHOTO) {
      sources = [
        attach.photo?.photoLargeEncrypted || '',
        attach.photo?.photoLarge || '',
        attach.photo?.thumbnail || '',
      ].filter(Boolean);
      break;
    } else if (attach.type === AttachType.VIDEO) {
      sources = [attach.video?.photoLarge || ''].filter(Boolean);
      break;
    }
  }

  return sources.map((src) => {
    if (!!~src.indexOf('data:image') || !!~src.indexOf('https')) {
      return src;
    } else {
      return aesDecrypt(src, decryptKey);
    }
  });
};

export function isOnlyEmoji(text: string) {
  const textWithoutEmoji = text.replace(EMOJI_REGEX, '').trim();
  return !textWithoutEmoji.length;
}

export enum DisplayMode {
  BROWSER = 'browser',
  STANDALONE = 'standalone',
}

export const getDisplayMode = () => {
  let displayMode = DisplayMode.BROWSER;
  if (
    // @ts-ignore
    ('standalone' in window.navigator && window.navigator.standalone === true) ||
    window.matchMedia('(display-mode: standalone)').matches
  ) {
    displayMode = DisplayMode.STANDALONE;
  }
  return displayMode;
};

export const wikiLink = (path?: string, lang = 'en') => {
  return 'https://wiki.ton.place' + (lang === 'ru' ? '' : '/v/eng') + (path || '');
};

export const roundToDecimal = (num: number, decimalPlaces: number) => {
  const shift = Math.pow(10, decimalPlaces);
  return Math.round(num * shift) / shift;
};

export const subscriptionDiscountPrice = (price: number, discount: number) => {
  return roundToDecimal(price - (price * discount) / 100, 2);
};

export function isIsolated() {
  return false;
  // const userId = window.store.getState().auth.currentUserId;
  // const users = window.store.getState().main.users;
  // const user = users[userId];
  // if (user && user.isolated) {
  //   return true;
  // }
  //
  // return getStorageItem('isolated') === '1';
}

export function isSponsorAbTest(userId: number) {
  return false;
  //return userId % 2 === 1;
}

export function openUrlWithoutReferrer(url: string, external?: boolean) {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noreferrer';

  if (external) link.target = '_blank';

  link.click();
}

/**
 * Отправляет событие в BigQuery для аналитики
 * @param name - Название события (например: 'session_started', 'page_viewed')
 * @param params - Дополнительные параметры события
 *
 * @example
 * // Событие начала сессии
 * sendEvent('session_started');
 *
 * // Событие просмотра страницы
 * sendEvent('page_viewed', {
 *   page_type: 'profile',
 *   user_id: 123
 * });
 */
export function buildEventPayload(name: string, params: any = {}) {
  const eventData: any = {
    event_name: name,
    event_data: params,
    client_timestamp: Math.floor(Date.now() / 1000),
    auth_ts: Math.floor(window.sessionStartedAt / 1000),
  };

  eventData.page = window.location.pathname;

  eventData.visitor_id = getStorageItem('visitor_id') || '';

  eventData.start_app = WebApp.initDataUnsafe?.start_param || '';

  // Получаем UTM параметры из URL
  const urlParams = new URLSearchParams(window.location.search);
  eventData.utm_source = urlParams.get('utm_source') || '';
  eventData.utm_medium = urlParams.get('utm_medium') || '';
  eventData.utm_campaign = urlParams.get('utm_campaign') || '';
  eventData.utm_term = urlParams.get('utm_term') || '';
  eventData.utm_content = urlParams.get('utm_content') || '';

  return eventData;
}

export function sendEvent(name: string, params: any = {}) {
  // Отправляем событие
  Api.post('/stats/bigquery_event', buildEventPayload(name, params)).catch((error) => {
    console.error('sendEvent error:', error);
  });
}

export * from './date';
export * from './color';
export * from './asyncStorage';
export * from './texts';
export * from './device';
export * from './anime';
export * from './elems';
export * from './crypto';
export * from './image';
export * from './utm';
export * from './ref';
export * from './transliterate';
export * from './payFormPatch';
export * from './attachmentToViewerItem';
export * from './attach';
export * from './getDocumentHref';
export * from './test';
export * from './confirm';
export * from './prompt';
export * from './downloadFile';
export * from './ads';
